import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@artstorefronts/ui';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { sanitizeRedirectPath } from '@arthelper/shared/safe-redirect';

import { FeatureWizardStep } from './FeatureWizardStep';
import type { WizardPresentation } from './types';
import type { WizardEvent, WizardRecorder } from './useWizardRecorder';

/**
 * The only failure copy the reader ever sees, and pressing the button again retries the call.
 * The key is the one town-square's host uses, from the shared catalog, so the two overlays
 * cannot drift into two different apologies for the same failure.
 */
const RETRY_MESSAGE_KEY = 'feature-wizard.retry-message';

/** The progress counter, localized here rather than in the shared step component. */
const PROGRESS_KEY = 'feature-wizard.progress';

/**
 * A hand-off is authored as either an app path or an absolute https:// address, and both have
 * to work: the wizard that teaches a town-square feature hands off across the origin.
 */
function handOff(href: string | null, navigate: NavigateFunction): void {
  if (!href) return;
  if (/^https:\/\//i.test(href)) {
    window.location.assign(href);
    return;
  }
  const path = sanitizeRedirectPath(href, undefined);
  if (!path) return;
  navigate(path);
}

/**
 * The overlay itself: the dialog chrome this app owns, wrapped around the SHARED step
 * component that both hosts and the authoring preview render, so one authored wizard looks the
 * same everywhere. It is mounted only for a wizard-audience viewer with steps to show.
 *
 * What a primary press MEANS lives here, because it is the host's decision: on any step but the
 * last it records the submission and advances, and never navigates. On the last step it records
 * the submission and then the completion, drops the parameter, closes, and only then follows the
 * wizard's hand-off if it has one. A wizard without one finishes by simply closing — still a
 * COMPLETED, never a DISMISSED, because the absence of a hand-off must not read as backing out.
 *
 * Exactly one terminal event is ever recorded, and a reader who closes always wins: the last
 * step's press is marked terminal before any call leaves, so a close behind it cannot add a
 * DISMISSED, and a completion landing after that close records the outcome without navigating.
 *
 * Only the panel's own close button can back out. The backdrop and Esc are refused, because a
 * DISMISSED is terminal — the link never opens again — and a stray click past the panel must not
 * spend a one-shot wizard.
 */
export function WizardOverlay({
  wizard,
  recorder,
  onClose,
  onStrip,
}: {
  wizard: WizardPresentation;
  recorder: WizardRecorder;
  onClose: () => void;
  onStrip: () => void;
}) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [isBusy, setIsBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** Positions whose submission already landed, so a retry sends the failed call alone. */
  const submittedRef = useRef(new Set<number>());
  /** The last step's button has been pressed, so a later close is not the reader backing out. */
  const terminalRef = useRef(false);
  /** The reader closed the overlay themselves, so a completion landing late must not navigate. */
  const closedByReaderRef = useRef(false);
  /** A press is still working through its calls, and will chase its own retries. */
  const runningRef = useRef(false);
  /** COMPLETED has landed: there is nothing left for a close to take over. */
  const completedRef = useRef(false);
  /**
   * The funnel's own order — ARRIVED → OPENED → STEP_VIEWED(1) — is the order an Amplitude funnel
   * is read in, so the events nobody is waiting on are chained rather than fired side by side.
   */
  const backgroundRef = useRef<Promise<unknown>>(Promise.resolve());

  const total = wizard.steps.length;
  const step = wizard.steps[index];
  const position = step?.position;
  const isLast = index === total - 1;

  const chase = useCallback(
    (event: WizardEvent) => {
      backgroundRef.current = backgroundRef.current.then(() => recorder.recordWithRetry(event));
    },
    [recorder]
  );

  useEffect(() => {
    chase({ eventType: 'OPENED' });
  }, [chase]);

  useEffect(() => {
    if (position == null) return;
    chase({ eventType: 'STEP_VIEWED', stepPosition: position });
  }, [chase, position]);

  /**
   * The completion, retried on the host's own initiative rather than by the reader: it outlives
   * the overlay, because the recorder is the landing's and a reader who has finished must not
   * leave a row that reads as having been abandoned. Only once it lands does the parameter go.
   */
  const finish = useCallback(async () => {
    const completed = await recorder.recordWithRetry({ eventType: 'COMPLETED' });
    if (!completed.ok) {
      setErrorMessage(t(RETRY_MESSAGE_KEY));
      setIsBusy(false);
      return;
    }
    completedRef.current = true;
    onStrip();
    onClose();
    // The reader closed the overlay while this was still landing: the outcome is a COMPLETED
    // either way, but following the hand-off would navigate someone who has already backed out.
    if (closedByReaderRef.current) return;
    handOff(wizard.handoffHref, navigate);
  }, [navigate, onClose, onStrip, recorder, t, wizard.handoffHref]);

  /**
   * The reader asked to leave, so the overlay goes now and the recording chases it: refusing to
   * close over a lost event would be worse than losing it. The parameter is dropped only once
   * DISMISSED lands, so if every retry fails the row stays non-terminal and the link may open
   * again later — the safe direction to fail in.
   */
  const dismiss = useCallback(() => {
    closedByReaderRef.current = true;
    onClose();

    // Closing once the last step's button has been pressed is still a COMPLETED, never a
    // DISMISSED: the absence of a hand-off, or a completion still in flight, must not read as
    // the reader having backed out.
    if (terminalRef.current) {
      // A completion that already failed has nobody left to press the button, so the close takes
      // it over and keeps retrying in the background rather than leaving the row non-terminal.
      if (!completedRef.current && !runningRef.current) void finish();
      return;
    }

    recorder.recordWithRetry({ eventType: 'DISMISSED' }).then((result) => {
      if (result.ok) onStrip();
    });
  }, [finish, onClose, onStrip, recorder]);

  const press = useCallback(async () => {
    if (position == null) return;
    setIsBusy(true);
    setErrorMessage(null);

    // A submission that already landed is never re-sent: a retry after a failed completion
    // sends the completion alone rather than appending a second step row for nothing.
    const submitted = submittedRef.current.has(position)
      ? { ok: true, permanent: false, stale: false }
      : await recorder.record({ eventType: 'STEP_SUBMITTED', stepPosition: position, payload: { ctaClicked: true } });

    // The steps were edited under this overlay. Stop, and leave the parameter in place so the
    // next visit fetches the wizard as it now stands.
    if (submitted.stale) {
      onClose();
      return;
    }
    if (!submitted.ok) {
      setErrorMessage(t(RETRY_MESSAGE_KEY));
      setIsBusy(false);
      return;
    }
    submittedRef.current.add(position);

    if (!isLast) {
      setIndex(index + 1);
      setIsBusy(false);
      return;
    }
    await finish();
  }, [finish, index, isLast, onClose, position, recorder, t]);

  const submit = useCallback(async () => {
    if (runningRef.current) return;
    // Marked terminal synchronously, before the first call is even sent: pressing the last step's
    // button IS finishing, so a close landing mid-flight must not also record a DISMISSED.
    if (isLast) terminalRef.current = true;
    runningRef.current = true;
    try {
      await press();
    } finally {
      runningRef.current = false;
    }
  }, [isLast, press]);

  if (!step) return null;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // The dialog's own close button is the only thing that reaches here: the backdrop and Esc
        // are refused below, so nothing else can report the reader as having backed out.
        if (!next) dismiss();
      }}
    >
      {/* No padding and no gap: the shared step component owns the panel's spacing, and the
          design's 460px cap is the panel's, not this app's default dialog width. */}
      <DialogContent
        className="gap-0 p-0 sm:max-w-[460px]"
        // Backing out is a decision, not a stray click. A wizard link fires once — a DISMISSED is
        // terminal and the link never opens again — so only the panel's own close button may spend
        // it, and a press outside the panel or on Esc leaves the wizard exactly where it was.
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <VisuallyHidden>
          <DialogTitle>{wizard.name}</DialogTitle>
          <DialogDescription>{step.config.headline}</DialogDescription>
        </VisuallyHidden>
        <FeatureWizardStep
          config={step.config}
          position={step.position}
          total={total}
          progressLabel={t(PROGRESS_KEY, { position: step.position, total })}
          isBusy={isBusy}
          errorMessage={errorMessage}
          onPrimary={submit}
        />
      </DialogContent>
    </Dialog>
  );
}
